import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import Hls from 'hls.js';
import { 
  Play, 
  Pause, 
  SkipBack, 
  SkipForward, 
  Volume2,
  VolumeX,
  AlertCircle,
  Copy,
  Settings
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { logIdPlumbing } from '@/features/swing-lab/lib/idPlumbingLogger';

interface VideoPlayerProps {
  signedUrl?: string;
  isLoading: boolean;
  error?: Error;
  onCopyUrl?: () => void;
  className?: string;
  videoRef?: React.RefObject<HTMLVideoElement>;
  onPlayStateChange?: (playing: boolean) => void;
  video?: any; // For determining playback policy
  onNativeVideo?: (el: HTMLVideoElement | null) => void;
}

const SPEED_PRESETS = [0.25, 0.5, 1, 1.5, 2];

export function VideoPlayer({ 
  signedUrl, 
  isLoading, 
  error,
  onCopyUrl,
  className = "",
  videoRef,
  onPlayStateChange,
  video,
  onNativeVideo
}: VideoPlayerProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);
  const [verbose] = useState(false); // Developer toggle for verbose logs
  
  const internalVideoRef = useRef<HTMLVideoElement>(null);
  const actualVideoRef = videoRef || internalVideoRef;
  const hlsRef = useRef<Hls | null>(null);
  const lastLoggedTokenRef = useRef<string | null>(null);
  const { toast } = useToast();

  // Stabilize player source computation
  const playerSrc = useMemo(() => {
    const isPublic = video?.playbackPolicy === 'public';
    
    if (isPublic && video?.muxPlaybackId) {
      return `https://stream.mux.com/${video.muxPlaybackId}.m3u8`;
    }
    
    if (!isPublic && signedUrl) {
      return signedUrl;
    }
    
    return null;
  }, [signedUrl, video?.playbackPolicy, video?.muxPlaybackId]);

  // Forward native video element to parent & log ID plumbing
  useEffect(() => {
    if (video) {
      logIdPlumbing('VideoPlayer props', {
        from: 'VideoPlayer',
        playbackId: video.muxPlaybackId,
        assetId: video.muxAssetId
      });
    }
    
    // Debug: Log player state for troubleshooting
    if (import.meta.env.DEV) {
      console.log('[VideoPlayer] Debug state:', {
        hasVideo: !!video,
        playbackId: video?.muxPlaybackId,
        playbackPolicy: video?.playbackPolicy,
        status: video?.status,
        hasSignedUrl: !!signedUrl,
        signedUrlLoading: !!isLoading,
        playerSrc,
        error: error?.message
      });
    }

    const resolveVideo = (): HTMLVideoElement | null => {
      const el = actualVideoRef.current;
      if (el) {
        el.crossOrigin = 'anonymous';
      }
      return el;
    };

    onNativeVideo?.(resolveVideo());
  }, [playerSrc, onNativeVideo, actualVideoRef, video, signedUrl, isLoading, error]);

  const handlePlay = useCallback(() => {
    if (actualVideoRef.current) {
      if (isPlaying) {
        actualVideoRef.current.pause();
      } else {
        actualVideoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  }, [isPlaying, actualVideoRef]);

  const handleTimeUpdate = useCallback(() => {
    if (actualVideoRef.current) {
      setCurrentTime(actualVideoRef.current.currentTime);
    }
  }, [actualVideoRef]);

  const handleLoadedMetadata = useCallback(() => {
    if (actualVideoRef.current) {
      setDuration(actualVideoRef.current.duration);
    }
  }, [actualVideoRef]);

  const handleSeek = useCallback((value: number[]) => {
    if (actualVideoRef.current) {
      actualVideoRef.current.currentTime = value[0];
      setCurrentTime(value[0]);
    }
  }, [actualVideoRef]);

  const handleVolumeChange = useCallback((value: number[]) => {
    if (actualVideoRef.current) {
      actualVideoRef.current.volume = value[0];
      setVolume(value[0]);
      setIsMuted(value[0] === 0);
    }
  }, [actualVideoRef]);

  const toggleMute = useCallback(() => {
    if (actualVideoRef.current) {
      actualVideoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  }, [isMuted, actualVideoRef]);

  // Frame stepping (±1 frame assuming 30fps)
  const stepFrame = useCallback((direction: 'forward' | 'backward') => {
    if (actualVideoRef.current) {
      const frameTime = 1/30; // Assume 30fps
      const newTime = direction === 'forward' 
        ? Math.min(duration, currentTime + frameTime)
        : Math.max(0, currentTime - frameTime);
      actualVideoRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  }, [currentTime, duration, actualVideoRef]);

  const handleSpeedChange = useCallback((speed: number) => {
    if (actualVideoRef.current) {
      actualVideoRef.current.playbackRate = speed;
      setPlaybackRate(speed);
      setShowSpeedMenu(false);
    }
  }, [actualVideoRef]);

  const handleCopyUrl = useCallback(() => {
    if (playerSrc) {
      navigator.clipboard.writeText(playerSrc);
      const urlType = video?.playbackPolicy === 'public' ? 'Direct URL' : 'Signed URL';
      toast({ title: "Copied!", description: `${urlType} copied to clipboard` });
      onCopyUrl?.();
    }
  }, [playerSrc, video?.playbackPolicy, toast, onCopyUrl]);

  // Dedupe the log (log once per token)
  useEffect(() => {
    if (!playerSrc) return;
    
    const isPublic = video?.playbackPolicy === 'public';
    
    if (isPublic) {
      // For public videos, log once if verbose
      if (verbose) {
        console.info('[VideoPlayer] Using direct playback URL for public video');
      }
    } else {
      // For signed URLs, extract token signature and log once per token
      try {
        const url = new URL(playerSrc);
        const token = url.searchParams.get('token') ?? '';
        const tokenSig = token.slice(-16); // lightweight signature
        
        if (lastLoggedTokenRef.current !== tokenSig) {
          lastLoggedTokenRef.current = tokenSig;
          console.info('[VideoPlayer] Using signed URL for private video (token …%s)', tokenSig);
        }
      } catch (e) {
        if (verbose) {
          console.warn('[VideoPlayer] Could not parse signed URL for logging:', e);
        }
      }
    }
  }, [playerSrc, video?.playbackPolicy, verbose]);

  // Setup HLS when player source changes
  useEffect(() => {
    if (!actualVideoRef.current || !playerSrc) return;

    const videoElement = actualVideoRef.current;
    
    // Clean up existing HLS instance
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    // Check if URL is HLS (.m3u8)
    if (playerSrc.includes('.m3u8')) {
      if (Hls.isSupported()) {
        if (verbose) {
          console.log('[VideoPlayer] Setting up HLS for URL:', playerSrc);
        }
        const hls = new Hls({
          debug: false,
          enableWorker: true,
        });
        
        hls.loadSource(playerSrc);
        hls.attachMedia(videoElement);
        
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          if (verbose) {
            console.log('[VideoPlayer] HLS manifest loaded successfully');
          }
        });
        
        hls.on(Hls.Events.ERROR, (event, data) => {
          console.error('[VideoPlayer] HLS error:', event, data);
          if (data.fatal) {
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                console.log('[VideoPlayer] Fatal network error, trying to recover...');
                hls.startLoad();
                break;
              case Hls.ErrorTypes.MEDIA_ERROR:
                console.log('[VideoPlayer] Fatal media error, trying to recover...');
                hls.recoverMediaError();
                break;
              default:
                console.log('[VideoPlayer] Fatal error, destroying HLS...');
                hls.destroy();
                break;
            }
          }
        });
        
        hlsRef.current = hls;
      } else if (videoElement.canPlayType('application/vnd.apple.mpegurl')) {
        // Safari supports HLS natively
        if (verbose) {
          console.log('[VideoPlayer] Using native HLS support');
        }
        videoElement.src = playerSrc;
      } else {
        console.error('[VideoPlayer] HLS not supported in this browser');
      }
    } else {
      // Regular video file
      if (verbose) {
        console.log('[VideoPlayer] Setting regular video source:', playerSrc);
      }
      videoElement.src = playerSrc;
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [playerSrc, verbose]);

  useEffect(() => {
    if (actualVideoRef.current && playerSrc) {
      actualVideoRef.current.playbackRate = playbackRate;
    }
  }, [playerSrc, playbackRate, actualVideoRef]);

  if (isLoading) {
    return (
      <div className={cn("rounded-lg border border-slate-200 bg-white p-8", className)}>
        <div className="text-center">
          <div className="w-16 h-16 bg-slate-100 rounded-lg mx-auto mb-3 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
          </div>
          <h3 className="font-semibold mb-2">Loading Video</h3>
          <p className="text-sm text-slate-600">
            Generating signed playback URL...
          </p>
        </div>
      </div>
    );
  }

  if (error || !playerSrc) {
    // Show different message for videos still processing vs actual errors
    const isProcessing = video?.status === 'uploading' || video?.status === 'processing';
    const errorMessage = error?.message || 
      (isProcessing ? 'Video is still processing...' : 'Unable to load video for playback');
    
    return (
      <div className={cn("rounded-lg border border-slate-200 bg-white p-8", className)}>
        <div className="text-center">
          <div className="w-16 h-16 bg-slate-100 rounded-lg mx-auto mb-3 flex items-center justify-center">
            <AlertCircle className="h-8 w-8 text-slate-500" />
          </div>
          <h3 className="font-semibold mb-2">
            {isProcessing ? 'Processing Video' : 'Video Unavailable'}
          </h3>
          <p className="text-sm text-slate-600 mb-4">
            {errorMessage}
          </p>
          {isProcessing && (
            <p className="text-xs text-slate-500">
              This usually takes 1-2 minutes. The page will update automatically.
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={cn("rounded-lg border border-slate-200 bg-white overflow-hidden", className)}>
      {/* Video Container */}
      <div className="relative bg-black aspect-video">
        <video
          ref={actualVideoRef}
          className="w-full h-full"
          crossOrigin="anonymous"
          playsInline
          webkit-playsinline="true"
          preload="metadata"
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onPlay={() => {
            setIsPlaying(true);
            onPlayStateChange?.(true);
          }}
          onPause={() => {
            setIsPlaying(false);
            onPlayStateChange?.(false);
          }}
          onEnded={() => {
            setIsPlaying(false);
            onPlayStateChange?.(false);
          }}
          onError={(e) => {
            console.error('[VideoPlayer] Video error:', e);
          }}
          onLoadStart={() => {
            if (verbose) {
              console.log('[VideoPlayer] Loading video...');
            }
          }}
        />
        
        {/* Video Controls Overlay */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
          <div className="space-y-3">
            {/* Progress Bar */}
            <Slider
              value={[currentTime]}
              max={duration}
              step={0.1}
              onValueChange={handleSeek}
              className="w-full"
            />
            
            {/* Control Buttons */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => stepFrame('backward')}
                  className="text-white hover:text-emerald-400 h-8 w-8 p-0"
                  title="Previous frame"
                >
                  <SkipBack className="h-4 w-4" />
                </Button>
                
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handlePlay}
                  className="text-white hover:text-emerald-400 h-8 w-8 p-0"
                >
                  {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                </Button>
                
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => stepFrame('forward')}
                  className="text-white hover:text-emerald-400 h-8 w-8 p-0"
                  title="Next frame"
                >
                  <SkipForward className="h-4 w-4" />
                </Button>
                
                {/* Volume Controls */}
                <div className="flex items-center space-x-2 ml-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={toggleMute}
                    className="text-white hover:text-emerald-400 h-8 w-8 p-0"
                  >
                    {isMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
                  </Button>
                  
                  <Slider
                    value={[isMuted ? 0 : volume]}
                    max={1}
                    step={0.1}
                    onValueChange={handleVolumeChange}
                    className="w-20"
                  />
                </div>

                {/* Speed Control */}
                <div className="relative ml-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowSpeedMenu(!showSpeedMenu)}
                    className="text-white hover:text-emerald-400 h-8 px-2 text-xs"
                  >
                    {playbackRate}×
                  </Button>
                  
                  {showSpeedMenu && (
                    <div className="absolute bottom-full mb-2 bg-black/90 rounded-md py-1 min-w-0">
                      {SPEED_PRESETS.map((speed) => (
                        <button
                          key={speed}
                          onClick={() => handleSpeedChange(speed)}
                          className="block w-full px-3 py-1 text-xs text-white hover:bg-white/20 text-left"
                        >
                          {speed}×
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <span className="text-sm text-white">
                  {Math.floor(currentTime / 60)}:{Math.floor(currentTime % 60).toString().padStart(2, '0')} / {Math.floor(duration / 60)}:{Math.floor(duration % 60).toString().padStart(2, '0')}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Copy URL Section */}
      <div className="p-4 border-t border-slate-200 bg-slate-50">
        <div className="flex items-center justify-between">
          <span className="text-sm text-slate-600">
            {video?.playbackPolicy === 'public' ? 'Direct playback URL' : 'Signed playback URL'}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={handleCopyUrl}
            className="h-8"
            disabled={!playerSrc}
          >
            <Copy className="h-3.5 w-3.5 mr-1" />
            Copy URL
          </Button>
        </div>
      </div>
    </div>
  );
}